/**
 * AL netværkstrafik i web/ går gennem dette modul — rå fetch() er forbudt
 * udenfor web/lib/ (håndhævet af ESLint). Wrapperen injicerer
 * X-Requested-With: XMLHttpRequest, som serverens CSRF-middleware (trin 4)
 * vil kræve på al mutation. Mønsteret etableres nu, så første komponent
 * fødes med det.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(status: number, message?: string, code?: string) {
    super(message ?? `API-fejl (${status})`);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

import {
  API_UNAUTHORIZED_EVENT,
  currentAuthenticationGeneration,
  type UnauthorizedEventDetail,
} from "./session-events";

export { API_UNAUTHORIZED_EVENT } from "./session-events";

export async function customFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const requestGeneration = currentAuthenticationGeneration();
  const headers = new Headers(init.headers);
  headers.set("X-Requested-With", "XMLHttpRequest");
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers,
  });
  if (response.status === 401) {
    window.dispatchEvent(
      new CustomEvent<UnauthorizedEventDetail>(API_UNAUTHORIZED_EVENT, {
        detail: { generation: requestGeneration },
      }),
    );
  }
  return response;
}

/** Serverens danske fejlbesked og stabile kode — til ApiError. */
async function fejlData(
  res: Response,
): Promise<{ message?: string; code?: string }> {
  try {
    const body = (await res.json()) as {
      error?: string;
      kode?: string;
      code?: string;
    };
    const code = body.kode ?? body.code;
    return {
      ...(body.error === undefined ? {} : { message: body.error }),
      ...(code === undefined ? {} : { code }),
    };
  } catch {
    return {};
  }
}

/** Returnér kun en konkret serverbesked, ikke ApiError-standardteksten. */
export function serverBesked(error: ApiError): string | null {
  return error.message.startsWith("API-fejl (") ? null : error.message;
}

export async function apiErrorFraResponse(res: Response): Promise<ApiError> {
  const error = await fejlData(res);
  return new ApiError(res.status, error.message, error.code);
}

export async function apiGet<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await customFetch(path, init);
  if (!res.ok) {
    throw await apiErrorFraResponse(res);
  }
  return (await res.json()) as T;
}

/** Mutationer (POST/PUT/PATCH/DELETE) — altid via denne. */
export async function apiSend<T>(
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (body !== undefined) headers.set("Content-Type", "application/json");
  const res = await customFetch(path, {
    ...init,
    method,
    headers,
    body: body === undefined ? null : JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFraResponse(res);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
